import { supabase, isConfigured } from './supabase';
import { mergeHdrPlans, type HdrCurrency, type HdrPlan } from '@rehub/pricing';

export interface HdrEntitlement {
  success: boolean;
  plan_id: string;
  plan_name: string;
  status: string;
  billing_currency: HdrCurrency | null;
  stripe_subscription_id: string | null;
  period_start: string;
  period_end: string;
  cancel_at_period_end: boolean;
  allowance: number;
  used: number;
  reserved: number;
  remaining: number;
  is_paid: boolean;
  is_low: boolean;
}

export async function getHdrPricingPlans(): Promise<HdrPlan[]> {
  if (!isConfigured) return mergeHdrPlans(null);
  const { data, error } = await supabase.rpc('get_hdr_pricing');
  if (error || !data?.success) return mergeHdrPlans(null);
  return mergeHdrPlans(data.plans);
}

export async function getMyHdrEntitlement(): Promise<HdrEntitlement | null> {
  if (!isConfigured) return null;
  const { data, error } = await supabase.rpc('get_my_hdr_entitlement');
  if (error || !data?.success) return null;
  return data as HdrEntitlement;
}

async function invokeBilling(body: Record<string, unknown>) {
  const { data: sessionData } = await supabase.auth.getSession();
  const token = sessionData.session?.access_token;
  if (!token) throw new Error('Not signed in');
  const { data, error } = await supabase.functions.invoke('hdrphoto-billing', {
    body,
    headers: { Authorization: `Bearer ${token}` },
  });
  if (error) throw new Error(error.message);
  if (data?.error) throw new Error(data.error);
  return data;
}

export async function startHdrCheckout(planId: string, currency: HdrCurrency) {
  return invokeBilling({ action: 'checkout', planId, currency });
}

export async function openHdrBillingPortal() {
  return invokeBilling({ action: 'portal' });
}

export async function cancelHdrSubscription() {
  return invokeBilling({ action: 'cancel' });
}

export interface HdrAdminPlanRow {
  id: string;
  name: string;
  sliderLabel: string;
  imageAllowance: number;
  priceGbpMinor: number;
  priceEurMinor: number;
  priceUsdMinor: number;
  stripePriceIdGbp: string | null;
  stripePriceIdEur: string | null;
  stripePriceIdUsd: string | null;
  isContact: boolean;
  isRecommended: boolean;
  isActive: boolean;
  displayOrder: number;
}

export async function adminGetHdrPlans(): Promise<{
  success: boolean;
  plans?: HdrAdminPlanRow[];
  processing_cost_usd?: number;
  gbp_usd_rate?: number;
  error?: string;
}> {
  const { data, error } = await supabase.rpc('admin_get_hdr_plans');
  if (error) return { success: false, error: error.message };
  return data;
}

export async function adminUpsertHdrPlan(patch: {
  id: string;
  imageAllowance?: number;
  priceGbpMinor?: number;
  priceEurMinor?: number;
  priceUsdMinor?: number;
  stripePriceIdGbp?: string | null;
  stripePriceIdEur?: string | null;
  stripePriceIdUsd?: string | null;
  isActive?: boolean;
  isRecommended?: boolean;
}) {
  const { data, error } = await supabase.rpc('admin_upsert_hdr_plan', {
    p_id: patch.id,
    p_image_allowance: patch.imageAllowance ?? null,
    p_price_gbp_minor: patch.priceGbpMinor ?? null,
    p_price_eur_minor: patch.priceEurMinor ?? null,
    p_price_usd_minor: patch.priceUsdMinor ?? null,
    p_stripe_price_id_gbp: patch.stripePriceIdGbp ?? null,
    p_stripe_price_id_eur: patch.stripePriceIdEur ?? null,
    p_stripe_price_id_usd: patch.stripePriceIdUsd ?? null,
    p_is_active: patch.isActive ?? null,
    p_is_recommended: patch.isRecommended ?? null,
  });
  if (error) return { success: false, error: error.message };
  return data;
}

export async function adminSetHdrSetting(key: 'processing_cost_usd' | 'gbp_usd_rate', value: string) {
  const { data, error } = await supabase.rpc('admin_set_hdr_setting', { p_key: key, p_value: value });
  if (error) return { success: false, error: error.message };
  return data;
}

export interface HdrProfitabilityMetrics {
  active_subscribers: number;
  mrr_gbp: number;
  mrr_eur: number;
  mrr_usd: number;
  mrr_usd_equivalent: number;
  subscribers_by_plan: Array<{
    plan_id: string;
    name: string;
    subscribers: number;
    images_this_month: number;
  }>;
  images_processed_this_month: number;
  avg_images_per_subscriber: number;
  processing_cost_usd: number;
  estimated_processing_cost_usd: number;
  revenue_usd_equivalent: number;
  gross_contribution_usd: number;
  gross_margin_pct: number;
  gbp_usd_rate: number;
}

export async function adminGetHdrProfitability() {
  const { data, error } = await supabase.rpc('admin_get_hdr_profitability');
  if (error) return { success: false as const, error: error.message };
  if (!data?.success) return { success: false as const, error: data?.error || 'Unknown error' };
  return { success: true as const, metrics: data.metrics as HdrProfitabilityMetrics };
}
